import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "@/contexts/SidebarContext";
import { PanelLeftClose, PanelLeft, X } from "lucide-react";
import { vociVisibili, vocePiuSpecifica } from "@/lib/menuLaterale";
import { useAuth } from "@/contexts/AuthContext";
import { useSchermoLargo } from "@/lib/useSchermo";

export default function Sidebar() {
  const { user } = useAuth() || {};
  const location = useLocation();
  const { collapsed: compresso, toggleCollapsed, mobileOpen, closeMobile } = useSidebar();
  // Sprint CRM-6 — sotto 1024px: menu a scomparsa, sempre con le scritte (la compressione vale solo da computer)
  const largo = useSchermoLargo();
  const collapsed = compresso && largo;
  // Sprint CRM-1 — livelli di accesso alle pipeline (regola D del menu)
  const [crm, setCrm] = useState(null);
  useEffect(() => {
    if (!user?.id) return;
    import("@/lib/api").then(({ api }) => api.get("/crm/me")).then((r) => setCrm(r.data)).catch(() => setCrm(null));
  }, [user?.id]);
  const voci = vociVisibili(user, crm);
  const attiva = vocePiuSpecifica(voci, location.pathname, location.search);
  // close mobile drawer on navigate
  useEffect(() => { closeMobile(); /* eslint-disable-next-line */ }, [location.pathname, location.search]);

  // When collapsed, nav items show only icon + tooltip (title); when expanded, show label
  const navItem = (voce, extra) => {
    const { to, label, icon: Icon, testid } = voce;
    const active = attiva === voce;
    const base = `flex items-center ${collapsed ? "justify-center px-0 py-2.5" : "gap-2.5 px-3 py-2"} rounded-md text-sm font-semibold tree-node relative group border-l-[3px] transition-colors`;
    const color = active ? "border-sky-400 bg-slate-700 text-white" : "border-transparent text-slate-200 hover:bg-slate-800 hover:text-white";
    return (
      <Link
        key={to}
        to={to}
        onClick={closeMobile}
        title={collapsed ? label : undefined}
        className={`${base} ${color}`}
        aria-current={active ? "page" : undefined}
        data-active={active ? "true" : "false"}
        data-testid={testid}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="flex-1 truncate">{label}</span>}
        {!collapsed && extra}
        {collapsed && extra && <span className="absolute -top-0.5 -right-0.5">{extra}</span>}
        {collapsed && (
          <span className="pointer-events-none absolute left-full ml-2 z-50 px-2 py-1 text-xs rounded-md bg-slate-900 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            {label}
          </span>
        )}
      </Link>
    );
  };

  const widthClass = collapsed ? "w-16" : "w-72";

  const asideClasses = `
    ${widthClass}
    shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col h-screen overflow-y-auto lg:overflow-visible
    transition-[width] duration-200
    lg:sticky lg:top-0
    fixed lg:static top-0 left-0 z-40
    ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
    transform transition-transform
  `;

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-900/50 z-30"
          onClick={closeMobile}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside className={asideClasses} data-testid="sidebar">
        {/* Header with logo */}
        <div className={`${collapsed ? "p-2" : "p-4"} border-b border-slate-800 bg-slate-900 flex items-center justify-center relative`}>
          <img
            src="/logo-su-scuro.png"
            alt="KARGEST"
            className={`${collapsed ? "h-12" : "h-32"} w-auto object-contain select-none transition-all`}
            draggable="false"
            data-testid="sidebar-logo"
          />
          <button
            onClick={closeMobile}
            className="lg:hidden absolute top-2 right-2 text-slate-300 p-1 rounded hover:bg-slate-800 hover:text-white"
            data-testid="sidebar-close-mobile"
            aria-label="Chiudi menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Subtitle + collapse toggle (desktop only) */}
        <div className={`${collapsed ? "px-1 py-1.5 justify-center" : "px-4 py-2 justify-between"} border-b border-slate-800 bg-slate-900 flex items-center`}>
          {!collapsed && (
            <div className="font-heading text-[11px] uppercase tracking-wider text-slate-400">Dealer Management</div>
          )}
          <button
            onClick={toggleCollapsed}
            className="hidden lg:inline-flex p-1 rounded text-slate-300 hover:bg-slate-800 hover:text-white"
            title={collapsed ? "Espandi menu" : "Comprimi menu"}
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>

        {/* Voci di lavoro */}
        <div className={`${collapsed ? "p-2" : "p-3"} space-y-1`}>
          {voci.map((voce) => (
            <React.Fragment key={voce.to}>
              {/* Sprint CRM-1.1 / CRM-2A — separatore in rilievo prima delle voci con `separatore: true` */}
              {voce.separatore && <div className="my-4 mx-1 h-[2px] rounded bg-black/70 shadow-[0_1px_0_rgba(255,255,255,0.28)]"
                data-testid={`menu-separatore-${voce.to.replace(/\W+/g, "")}`} />}
              {navItem(voce)}
            </React.Fragment>
          ))}
        </div>

      </aside>
    </>
  );
}
